import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type { CreateAttachmentInput, Mutation } from '@/generated/graphql';
import * as Attachment from '@/types/Attachment';
import { AttachmentStatusEnum } from '@/generated/graphql';
import { AttachmentStatus } from '@/types/Attachment';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

const randomKey = () => {
  return Math.random().toString(16).substring(2);
};

const fileChecksum = async (file: Blob): Promise<string | undefined> => {
  if (!crypto.subtle) {
    return;
  }

  const fileContents = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', fileContents);
  const hashArray = Array.from(new Uint8Array(digest));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

export interface PendingAttachment {
  attachment: Attachment.Attachment;
  file: File;
  uploadProgress: number;
  metadataSaved?: boolean;
  abortController?: AbortController;
  previewData?: string;
  dataLoaded?: boolean;
}

interface AttachmentState {
  pendingAttachments: { [key: string]: PendingAttachment };
}

const CREATE_ATTACHMENT_MUTATION = gql`
  mutation SaveAttachmentMeta($attachment: CreateAttachmentInput!) {
    createAttachment(input: $attachment) {
      attachment {
        id
        accountId
        checksum
        derivedFromId
        mime
        name
        size
        status
      }
      clientMutationId
      errors
    }
  }
`;

const setPreviewData = (key: string, data?: string) => {
  const attStore = useAttachmentStore();
  if (!data || !attStore.pendingAttachments[key]) {
    return;
  }

  attStore.pendingAttachments[key].dataLoaded = true;
  attStore.pendingAttachments[key].previewData = data;
};

const setAttachmentProgress = (key: string, progress: number) => {
  const attStore = useAttachmentStore();
  if (!attStore.pendingAttachments[key]) {
    return;
  }

  attStore.pendingAttachments[key].uploadProgress = progress;
};

export const useAttachmentStore = defineStore('attachment', {
  state: (): AttachmentState => ({
    pendingAttachments: {},
  }),

  getters: {
    allAttachmentsReady(state: AttachmentState): boolean {
      return Object.keys(state.pendingAttachments).every(
        (k: string) =>
          state.pendingAttachments[k].attachment.status === Attachment.AttachmentStatus.READY ||
          state.pendingAttachments[k].attachment.status === Attachment.AttachmentStatus.FAILED,
      );
    },
  },

  actions: {
    // schedules files for upload and retruns their local keys
    saveFiles(files: FileList): string[] {
      const keys: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const key = this.addPendingAttachment(file);
        keys.push(key);
        this.savePendingAttachment(key);
      }
      return keys;
    },

    addPendingAttachment(file: File) {
      const localKey = randomKey();
      const pending: PendingAttachment = {
        attachment: {
          name: file.name,
          size: file.size,
          mime: file.type,
        },
        file,
        uploadProgress: 0,
      };

      this.pendingAttachments[localKey] = pending;

      const fileReader = new FileReader();
      fileReader.addEventListener('load', e => {
        setPreviewData(localKey, e.target?.result?.toString());
      });
      fileReader.readAsDataURL(file);

      return localKey;
    },

    async savePendingAttachment(key: string) {
      try {
        await this.savePendingAttachmentMeta(key);
        await this.uploadPendingAttachmentFile(key);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        this.pendingAttachments[key].attachment.status = AttachmentStatus.FAILED;
      } finally {
        //
      }
    },

    async savePendingAttachmentMeta(key: string) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      // TODO: Provide account_id

      const pendingAttachment = this.pendingAttachments[key];
      const { file } = pendingAttachment;

      pendingAttachment.attachment.checksum = await fileChecksum(file);

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: CREATE_ATTACHMENT_MUTATION,
          variables: {
            attachment: <CreateAttachmentInput>{
              attachment: {
                name: pendingAttachment.attachment.name,
                size: pendingAttachment.attachment.size,
                checksum: pendingAttachment.attachment.checksum,
                mime: pendingAttachment.attachment.mime,
              },
            },
          },
        });

        if (data?.createAttachment?.attachment) {
          Object.assign(pendingAttachment.attachment, Attachment.fromGql(data.createAttachment.attachment));
        }
        pendingAttachment.metadataSaved = true;
      } finally {
        //
      }
    },

    async uploadPendingAttachmentFile(key: string) {
      const connectionStore = useConnectionStore();
      const client = connectionStore.attachmentsClient;
      if (!client) {
        throw new Error('attachmentClient is not initialized');
      }

      const pendingAttachment = this.pendingAttachments[key];
      pendingAttachment.abortController = new AbortController();

      const formData = new FormData();
      formData.append('file[file]', pendingAttachment.file);
      this.pendingAttachments[key].attachment.status = Attachment.AttachmentStatus.UPLOADING;
      // TODO: Handle infinite pending state and display error message to user
      const { data } = await client.put<{
        url: string;
        status: AttachmentStatusEnum;
      }>(`/${pendingAttachment.attachment.id}`, formData, {
        signal: pendingAttachment.abortController.signal,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress(e: ProgressEvent) {
          const progress = e.loaded / e.total;
          setAttachmentProgress(key, progress * 100.0);
        },
      });

      const { url, status } = data;
      this.pendingAttachments[key].attachment.status = Attachment.gqlStatusMap[status];
      this.pendingAttachments[key].attachment.url = url;
    },
    getPendingAttachmentId(key: string): number | null {
      const pendingAttachment = this.pendingAttachments[key];
      if (!pendingAttachment) {
        return null;
      }
      return Number(pendingAttachment.attachment?.id) || null;
    },
    removeAllUploadedAttachments(): void {
      const updatedPendingAttachments = Object.entries(this.pendingAttachments).filter(
        ([_, value]) => !(value.uploadProgress === 100 && value.metadataSaved),
      );
      this.pendingAttachments = updatedPendingAttachments;
    },
  },
});
